import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createAgreement,
  updateAgreement,
  getAgreement,
  uploadAgreementDocument,
  deleteAgreementDocument,
  downloadAgreementDocument,
  convertFormula,
} from '../services/agreementService';
import { saveDraft, getDraft, deleteDraft } from '../services/agreementDraftService';
import { listHospitals } from '../services/hospitalService';
import { listParameters } from '../services/parameterService';
import { listParamFormulas, upsertParamFormula } from '../services/paramFormulaService';
import type { ParamFormula } from '../services/paramFormulaService';
import type { Hospital, Parameter, AgreementDocumentInfo } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import FormulaTextArea from '../components/ui/FormulaTextArea';
import AgreementFormulaTestDialog from './AgreementFormulaTestDialog';

export default function AgreementFormPage() {
  const navigate = useNavigate();
  const { agreementId } = useParams<{ agreementId: string }>();
  const isEditMode = Boolean(agreementId);

  // Get draftId from query params
  const searchParams = new URLSearchParams(window.location.search);
  const draftIdParam = searchParams.get('draftId');

  // Draft state
  const [currentDraftId, setCurrentDraftId] = useState<number | null>(
    draftIdParam ? Number(draftIdParam) : null
  );
  const [draftSaveStatus, setDraftSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [originalAgreementId, setOriginalAgreementId] = useState<number | null>(
    isEditMode && agreementId ? Number(agreementId) : null
  );

  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [hospitalUuid, setHospitalUuid] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTill, setValidTill] = useState('');
  const [hospitalShareFormula, setHospitalShareFormula] = useState('');
  const [arApFormula, setArApFormula] = useState('');

  const [availableInputParams, setAvailableInputParams] = useState<Parameter[]>([]);
  const [selectedInputParamIds, setSelectedInputParamIds] = useState<number[]>([]);

  const [calcParams, setCalcParams] = useState<
    { parameterName: string; naturalLanguageFormula: string; executableFormula?: string }[]
  >([]);

  const [hospitalShareExecutable, setHospitalShareExecutable] = useState('');
  const [arApExecutable, setArApExecutable] = useState('');
  const [showHsTest, setShowHsTest] = useState(false);
  const [showArApTest, setShowArApTest] = useState(false);
  const [isConvertingHs, setIsConvertingHs] = useState(false);
  const [isConvertingArAp, setIsConvertingArAp] = useState(false);
  const [convertingCalcIndex, setConvertingCalcIndex] = useState<number | null>(null);
  const [testCalcIndex, setTestCalcIndex] = useState<number | null>(null);

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  // Separate error states for formula conversions
  const [hsConvertError, setHsConvertError] = useState<string | null>(null);
  const [arApConvertError, setArApConvertError] = useState<string | null>(null);
  const [calcConvertErrors, setCalcConvertErrors] = useState<Record<number, string>>({});

  const [documents, setDocuments] = useState<AgreementDocumentInfo[]>([]);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<number, string>>({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pending files selected before agreement is saved (create mode)
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const [paramSearchTerm, setParamSearchTerm] = useState('');
  const [hospitalSearch, setHospitalSearch] = useState('');
  const [hospitalDropdownOpen, setHospitalDropdownOpen] = useState(false);
  const hospitalDropdownRef = useRef<HTMLDivElement>(null);
  const dragIndexRef = useRef<number | null>(null);

  // Parameter list reload state
  const [isReloadingParams, setIsReloadingParams] = useState(false);
  const [lastParamReloadTime, setLastParamReloadTime] = useState<Date | null>(null);

  // Saved param formulas for @ autocomplete
  const [savedParamFormulas, setSavedParamFormulas] = useState<ParamFormula[]>([]);

  // Effective agreement ID — URL param in edit mode
  const effectiveAgreementId = agreementId ? Number(agreementId) : null;

  // Load thumbnail blob URLs for documents via authenticated API
  useEffect(() => {
    if (!effectiveAgreementId || documents.length === 0) return;
    let cancelled = false;
    const urls: Record<number, string> = {};

    (async () => {
      for (const doc of documents) {
          if (thumbnailUrls[doc.documentId]) continue;
          try {
            const blob = await downloadAgreementDocument(effectiveAgreementId, doc.documentId);
          if (cancelled) break;
          urls[doc.documentId] = URL.createObjectURL(blob);
        } catch {
          // skip failed thumbnails
        }
      }
      if (!cancelled && Object.keys(urls).length > 0) {
        setThumbnailUrls((prev) => ({ ...prev, ...urls }));
      }
    })();

    return () => {
      cancelled = true;
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [documents, effectiveAgreementId]);

  // Close hospital dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (hospitalDropdownRef.current && !hospitalDropdownRef.current.contains(e.target as Node)) {
        setHospitalDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    listHospitals({ limit: 1000 })
      .then((res) => setHospitals(res.hospitals))
      .catch(() => setError('Failed to load hospitals.'));

    listParameters({ type: 'INPUT', limit: 1000 })
      .then((res) => {
        setAvailableInputParams(res.parameters);
        
        // Auto-select SYSTEM parameters only if creating new agreement (not editing or loading draft)
        if (!isEditMode && !draftIdParam) {
          const systemParamIds = res.parameters
            .filter((p) => p.sourceType === 'SYSTEM')
            .map((p) => p.parameterId);
          setSelectedInputParamIds(systemParamIds);
        }
      })
      .catch(() => setError('Failed to load input parameters.'));

    listParamFormulas()
      .then((data) => setSavedParamFormulas(data))
      .catch(() => {/* non-critical, ignore */});

    // Load draft if draftId is present
    if (draftIdParam && !isEditMode) {
      setIsLoadingData(true);
      getDraft(Number(draftIdParam))
        .then((draft) => {
          if (draft.agreementId) setOriginalAgreementId(draft.agreementId);
          if (draft.hospitalUuid) setHospitalUuid(draft.hospitalUuid);
          if (draft.validFrom) setValidFrom(draft.validFrom.split('T')[0]);
          if (draft.validTill) setValidTill(draft.validTill.split('T')[0]);
          if (draft.hospitalShareNlFormula) setHospitalShareFormula(draft.hospitalShareNlFormula);
          if (draft.hospitalShareExecutable) setHospitalShareExecutable(draft.hospitalShareExecutable);
          if (draft.arApNlFormula) setArApFormula(draft.arApNlFormula);
          if (draft.arApExecutable) setArApExecutable(draft.arApExecutable);
          if (draft.inputParameterIds) setSelectedInputParamIds(draft.inputParameterIds);
          if (draft.calculatedParameters) {
            setCalcParams(draft.calculatedParameters.map(cp => ({
              parameterName: cp.parameterName,
              naturalLanguageFormula: cp.naturalLanguageFormula,
              executableFormula: cp.executableFormula,
            })));
          }
          
          // Load documents from original agreement if draft is linked
          if (draft.agreementId) {
            getAgreement(draft.agreementId)
              .then((agreement) => {
                if (agreement.documents) {
                  setDocuments(agreement.documents);
                }
              })
              .catch((err) => {
                console.error('Failed to load agreement documents:', err);
                // Non-critical error, don't show to user
              });
          }
        })
        .catch(() => setError('Failed to load draft.'))
        .finally(() => setIsLoadingData(false));
    }
  }, []);

  useEffect(() => {
    if (isEditMode && agreementId) {
      setIsLoadingData(true);
      getAgreement(Number(agreementId))
        .then((agreement) => {
          setHospitalUuid(agreement.hospitalUuid);
          setValidFrom(agreement.validFrom?.split('T')[0] || '');
          setValidTill(agreement.validTill?.split('T')[0] || '');
          setHospitalShareFormula(agreement.hospitalShareNlFormula || '');
          setArApFormula(agreement.arApNlFormula || '');
          setHospitalShareExecutable(agreement.hospitalShareExecutable || '');
          setArApExecutable(agreement.arApExecutable || '');
          if (agreement.inputParameters) {
            setSelectedInputParamIds(agreement.inputParameters.map((p) => p.parameterId));
          }
          if (agreement.calculatedParameters) {
            setCalcParams(
              agreement.calculatedParameters.map((cp) => ({
                parameterName: cp.parameterName,
                naturalLanguageFormula: cp.naturalLanguageFormula,
                executableFormula: cp.executableFormula,
              }))
            );
          }
          if (agreement.documents) {
            setDocuments(agreement.documents);
          }
        })
        .catch(() => setError('Failed to load agreement data.'))
        .finally(() => setIsLoadingData(false));
    }
  }, [isEditMode, agreementId]);

  function toggleInputParam(paramId: number) {
    setSelectedInputParamIds((prev) =>
      prev.includes(paramId) ? prev.filter((id) => id !== paramId) : [...prev, paramId]
    );
  }

  function removeInputParam(paramId: number) {
    setSelectedInputParamIds((prev) => prev.filter((id) => id !== paramId));
  }

  async function reloadParameterList() {
    setIsReloadingParams(true);
    try {
      const res = await listParameters({ type: 'INPUT', limit: 1000 });
      setAvailableInputParams(res.parameters);
      setLastParamReloadTime(new Date());
    } catch (err) {
      setError('Failed to reload parameters');
    } finally {
      setIsReloadingParams(false);
    }
  }

  function handleDragStart(index: number) {
    dragIndexRef.current = index;
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  function handleDrop(dropIndex: number) {
    const dragIndex = dragIndexRef.current;
    if (dragIndex === null || dragIndex === dropIndex) return;
    setSelectedInputParamIds((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(dropIndex, 0, moved);
      return updated;
    });
    dragIndexRef.current = null;
  }

  function addCalcParam() {
    setCalcParams((prev) => [...prev, { parameterName: '', naturalLanguageFormula: '' }]);
  }

  function removeCalcParam(index: number) {
    setCalcParams((prev) => prev.filter((_, i) => i !== index));
  }

  function updateCalcParam(index: number, field: 'parameterName' | 'naturalLanguageFormula' | 'executableFormula', value: string) {
    setCalcParams((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  }

  function getReferencedParams(formula: string): string[] {
    if (!formula.trim()) return [];
    const knownNames = new Set<string>([
      ...availableInputParams.map((p) => p.parameterName),
      ...calcParams.map((p) => p.parameterName).filter(Boolean),
    ]);
    const words = formula.match(/\b[A-Za-z_][A-Za-z0-9_]*\b/g) || [];
    return [...new Set(words.filter((w) => knownNames.has(w)))];
  }

  function getKnownVariableNames(): string[] {
    // TODO: Fetch system variables dynamically from backend instead of hardcoding
    // Commented out to avoid outdated/incorrect variable names in autocomplete
    // const systemVars = [
    //   'Hospital_ID', 'Hospital_Name', 'OT_Date', 'OT_Duration',
    //   'Patient_Name', 'Patient_Age', 'Doctor_Name', 'Payment_Mode',
    //   'Gross_Bill', 'Discount_Amount', 'Net_Bill_Amount', 'Collection_Amount',
    // ];
    const systemVars: string[] = [];
    
    const inputNames = selectedInputParamIds
      .map((id) => availableInputParams.find((p) => p.parameterId === id))
      .filter(Boolean)
      .map((p) => p!.parameterName);
    const calcNames = calcParams.map((p) => p.parameterName).filter(Boolean);
    const savedNames = savedParamFormulas.map((f) => f.paramName);
    return [...new Set([...systemVars, ...inputNames, ...calcNames, ...savedNames])];
  }

  // Build a map of paramName -> naturalLanguageFormula for FormulaTextArea
  function getSavedFormulasMap(): Record<string, string> {
    const map: Record<string, string> = {};
    for (const f of savedParamFormulas) {
      map[f.paramName] = f.naturalLanguageFormula;
    }
    return map;
  }

  function getConvertContext() {
    const inputNames = selectedInputParamIds
      .map((id) => availableInputParams.find((p) => p.parameterId === id))
      .filter(Boolean)
      .map((p) => p!.parameterName);
    const calcNames = calcParams.map((p) => p.parameterName).filter(Boolean);
    return { inputParameterNames: inputNames, calculatedParameterNames: calcNames };
  }

  async function handleConvertHospitalShare() {
    if (!hospitalShareFormula.trim()) return;
    setIsConvertingHs(true);
    setHsConvertError(null);
    try {
      const result = await convertFormula({ formula: hospitalShareFormula, ...getConvertContext() });
      setHospitalShareExecutable(result.executableFormula);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to convert formula.';
      setHsConvertError(msg);
    } finally {
      setIsConvertingHs(false);
    }
  }

  async function handleConvertArAp() {
    if (!arApFormula.trim()) return;
    setIsConvertingArAp(true);
    setArApConvertError(null);
    try {
      const result = await convertFormula({ formula: arApFormula, ...getConvertContext() });
      setArApExecutable(result.executableFormula);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to convert formula.';
      setArApConvertError(msg);
    } finally {
      setIsConvertingArAp(false);
    }
  }

  async function handleConvertCalcParam(index: number) {
    const cp = calcParams[index];
    if (!cp || !cp.naturalLanguageFormula.trim()) return;
    
    // Check if parameter name already exists in saved formulas (case-insensitive)
    if (cp.parameterName.trim()) {
      const existingFormula = savedParamFormulas.find(f => f.paramName.toLowerCase() === cp.parameterName.toLowerCase());
      if (existingFormula) {
        // Show error that name already exists
        setCalcConvertErrors((prev) => ({ 
          ...prev, 
          [index]: `Parameter name "${cp.parameterName}" already exists in formula library. Please use a unique name.` 
        }));
        return;
      }
    }
    
    setConvertingCalcIndex(index);
    setCalcConvertErrors((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
    try {
      const result = await convertFormula({ formula: cp.naturalLanguageFormula, ...getConvertContext() });
      const executableFormula = result.executableFormula;
      
      // Update state with executable formula
      setCalcParams((prev) => prev.map((p, i) => (i === index ? { ...p, executableFormula } : p)));
      
      // Immediately save to param formula library
      if (cp.parameterName.trim()) {
        try {
          const savedFormula = await upsertParamFormula({
            paramName: cp.parameterName,
            naturalLanguageFormula: cp.naturalLanguageFormula,
            executableFormula: executableFormula,
          });
          console.log(`Param formula saved to library: ${cp.parameterName}`);
          
          // Update savedParamFormulas state so autocomplete shows the new formula
          setSavedParamFormulas((prev) => {
            // Add new formula (we already checked it doesn't exist)
            return [...prev, savedFormula];
          });
        } catch (saveErr) {
          console.error('Failed to save param formula to library:', saveErr);
          // Non-critical error, continue
        }
      }
      
      // Save draft after converting formula
      await performDraftSave(true, index);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to convert formula.';
      setCalcConvertErrors((prev) => ({ ...prev, [index]: msg }));
    } finally {
      setConvertingCalcIndex(null);
    }
  }

  async function performDraftSave(fromConvertButton: boolean = false, calcParamIndex?: number) {
    console.log('Performing draft save...');
    
    // Validate hospital is selected before saving draft
    if (!hospitalUuid) {
      if (fromConvertButton && calcParamIndex !== undefined) {
        // Show error on the calculated parameter
        setCalcConvertErrors((prev) => ({ 
          ...prev, 
          [calcParamIndex]: 'Please select a hospital before converting formula.' 
        }));
      } else {
        // Show error at the top
        setError('Please select a hospital before saving draft.');
      }
      return;
    }
    
    setDraftSaveStatus('saving');
    
    try {
      const filteredCalcParams = calcParams
        .filter((p) => p.parameterName.trim() && p.naturalLanguageFormula.trim())
        .map((p) => ({
          parameterName: p.parameterName,
          naturalLanguageFormula: p.naturalLanguageFormula,
          executableFormula: p.executableFormula,
        }));

      const draftData = {
        draftId: currentDraftId || undefined,
        agreementId: originalAgreementId || undefined,
        hospitalUuid: hospitalUuid || undefined,
        validFrom: validFrom || undefined,
        validTill: validTill || undefined,
        hospitalShareNlFormula: hospitalShareFormula || undefined,
        hospitalShareExecutable: hospitalShareExecutable || undefined,
        arApNlFormula: arApFormula || undefined,
        arApExecutable: arApExecutable || undefined,
        inputParameterIds: selectedInputParamIds.length > 0 ? selectedInputParamIds : undefined,
        calculatedParameters: filteredCalcParams.length > 0 ? filteredCalcParams : undefined,
      };

      console.log('Saving draft with data:', draftData);
      const draft = await saveDraft(draftData);
      console.log('Draft saved successfully:', draft);

      setCurrentDraftId(draft.draftId);
      setDraftSaveStatus('saved');
      
      // Reset status after 2 seconds
      setTimeout(() => {
        setDraftSaveStatus('idle');
      }, 2000);
    } catch (err) {
      console.error('Failed to save draft:', err);
      setDraftSaveStatus('idle');
    } finally {
      // No cleanup needed
    }
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Only PNG, JPEG, and PDF files are accepted.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 10 MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // If agreement not yet saved, store file locally
    if (!effectiveAgreementId) {
      setPendingFiles((prev) => [...prev, file]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    setError(null);
    try {
      const doc = await uploadAgreementDocument(effectiveAgreementId, file);
      setDocuments((prev) => [...prev, doc]);
      // Create thumbnail URL from the local file immediately
      const localUrl = URL.createObjectURL(file);
      setThumbnailUrls((prev) => ({ ...prev, [doc.documentId]: localUrl }));
    } catch {
      setError('Failed to upload document.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDeleteDocument(documentId: number) {
    if (!effectiveAgreementId) return;
    try {
      await deleteAgreementDocument(effectiveAgreementId, documentId);
      setDocuments((prev) => prev.filter((d) => d.documentId !== documentId));
      // Revoke thumbnail blob URL
      if (thumbnailUrls[documentId]) {
        URL.revokeObjectURL(thumbnailUrls[documentId]);
        setThumbnailUrls((prev) => {
          const updated = { ...prev };
          delete updated[documentId];
          return updated;
        });
      }
    } catch {
      setError('Failed to delete document.');
    }
  }

  async function handleDownloadDocument(documentId: number, fileName: string) {
    if (!effectiveAgreementId) return;
    try {
      const blob = await downloadAgreementDocument(effectiveAgreementId, documentId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download document.');
    }
  }

  function validate(): boolean {
    const errors: Record<string, string> = {};
    if (!hospitalUuid) errors.hospitalUuid = 'Hospital is required';
    if (!validFrom) errors.validFrom = 'Valid From date is required';
    if (!validTill) errors.validTill = 'Valid Till date is required';
    if (validFrom && validTill && validTill < validFrom) {
      errors.validTill = 'Valid Till must be on or after Valid From';
    }
    if (!hospitalShareFormula.trim()) errors.hospitalShareFormula = 'Hospital Share formula is required';
    if (!arApFormula.trim()) errors.arApFormula = 'AR/AP formula is required';
    // Document validation removed - documents are now optional

    // Validate calculated parameter names have no spaces
    for (const cp of calcParams) {
      if (cp.parameterName.trim() && /\s/.test(cp.parameterName.trim())) {
        errors.calcParamNames = 'Calculated parameter names must not contain spaces. Use underscores or camelCase (e.g., AnaesthesiaCharges)';
        break;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    console.log('=== FORM SUBMIT DEBUG ===');
    console.log('isEditMode:', isEditMode);
    console.log('agreementId:', agreementId);
    console.log('originalAgreementId:', originalAgreementId);
    console.log('hospitalUuid:', hospitalUuid);
    console.log('validFrom:', validFrom);
    console.log('validTill:', validTill);

    if (!validate()) {
      console.log('Validation failed');
      return;
    }

    console.log('Validation passed, submitting...');

    setIsSubmitting(true);
    setSubmitProgress('Processing formulas...');
    try {
      const filteredCalcParams = calcParams
        .filter((p) => p.parameterName.trim() && p.naturalLanguageFormula.trim())
        .map((p) => ({ parameterName: p.parameterName, naturalLanguageFormula: p.naturalLanguageFormula }));

      // Check if this draft is linked to an existing agreement
      if (originalAgreementId && !isEditMode) {
        setSubmitProgress('Updating agreement...');
        // Update existing agreement instead of creating new one
        await updateAgreement(originalAgreementId, {
          validFrom,
          validTill,
          hospitalShareNlFormula: hospitalShareFormula,
          arApNlFormula: arApFormula,
          inputParameterIds: selectedInputParamIds,
          calculatedParameters: filteredCalcParams,
        });
        
        // Delete draft after successful update
        if (currentDraftId) {
          try {
            await deleteDraft(currentDraftId);
          } catch (err) {
            console.error('Failed to delete draft:', err);
            // Non-critical, continue
          }
        }
        
        setSuccess('Agreement updated successfully.');
        setTimeout(() => navigate('/agreements'), 1500);
      } else if (isEditMode && agreementId) {
        setSubmitProgress('Updating agreement...');
        await updateAgreement(Number(agreementId), {
          validFrom,
          validTill,
          hospitalShareNlFormula: hospitalShareFormula,
          arApNlFormula: arApFormula,
          inputParameterIds: selectedInputParamIds,
          calculatedParameters: filteredCalcParams,
        });
        
        // Delete draft after successful update
        if (currentDraftId) {
          try {
            await deleteDraft(currentDraftId);
          } catch (err) {
            console.error('Failed to delete draft:', err);
            // Non-critical, continue
          }
        }
        
        setSuccess('Agreement updated successfully.');
        setTimeout(() => navigate('/agreements'), 1500);
      } else {
        setSubmitProgress('Creating agreement...');
        const newAgreement = await createAgreement({
          hospitalUuid,
          validFrom,
          validTill,
          hospitalShareNlFormula: hospitalShareFormula,
          arApNlFormula: arApFormula,
          inputParameterIds: selectedInputParamIds,
          calculatedParameters: filteredCalcParams,
        });

        // Upload pending files now that we have an agreement ID
        if (pendingFiles.length > 0) {
          setSubmitProgress(`Uploading ${pendingFiles.length} document(s)...`);
          for (const file of pendingFiles) {
            await uploadAgreementDocument(newAgreement.agreementId, file);
          }
        }
        
        // Delete draft after successful creation
        if (currentDraftId) {
          try {
            await deleteDraft(currentDraftId);
          } catch (err) {
            console.error('Failed to delete draft:', err);
            // Non-critical, continue
          }
        }

        setSuccess('Agreement created successfully.');
        setTimeout(() => navigate('/agreements'), 1500);
      }
    } catch (err: unknown) {
      setSubmitProgress('');
      const axiosErr = err as { response?: { status?: number; data?: { error?: string } } };
      const status = axiosErr.response?.status;
      const message = axiosErr.response?.data?.error;

      if (status === 409) {
        setError(message || 'An agreement with overlapping dates already exists for this hospital.');
      } else if (status === 422) {
        setError(message || 'Formula conversion failed. Please rephrase your formula.');
      } else if (status === 400) {
        setError(message || 'Validation error. Please check your input.');
      } else {
        setError(message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
      setSubmitProgress('');
    }
  }

  if (isLoadingData) {
    return <LoadingSpinner message="Loading agreement data..." />;
  }

  return (
    <div className="agreement-form-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h2>{isEditMode ? 'Edit Agreement' : 'Create Agreement'}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button 
            onClick={() => performDraftSave()} 
            variant="secondary"
            size="sm"
            disabled={draftSaveStatus === 'saving'}
          >
            Save Draft Now
          </Button>
          {draftSaveStatus !== 'idle' && (
            <div style={{
              fontSize: '0.875rem',
              padding: '0.25rem 0.75rem',
              borderRadius: '4px',
              backgroundColor: draftSaveStatus === 'saving' ? '#fef3c7' : '#d1fae5',
              color: draftSaveStatus === 'saving' ? '#92400e' : '#065f46',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {draftSaveStatus === 'saving' ? (
                <>
                  <span style={{ 
                    display: 'inline-block',
                    animation: 'spin 1s linear infinite'
                  }}>⟳</span>
                  Saving draft...
                </>
              ) : (
                <>
                  <span>✓</span>
                  Draft saved
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" onDismiss={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Basic Info Section */}
        <fieldset>
          <legend>Basic Information</legend>

          <div className="basic-info-grid">
            <div className="hospital-field">
              <div className={`form-field ${fieldErrors.hospitalUuid ? 'form-field-error' : ''}`}>
                <label>Hospital</label>
                <div ref={hospitalDropdownRef} style={{ position: 'relative' }}>
                  <div
                    onClick={() => { if (!isEditMode) setHospitalDropdownOpen(!hospitalDropdownOpen); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '0.5rem 0.75rem',
                      cursor: isEditMode ? 'not-allowed' : 'pointer',
                      backgroundColor: isEditMode ? '#f3f4f6' : '#fff',
                      fontSize: '0.9rem',
                      color: hospitalUuid ? '#111827' : '#9ca3af',
                    }}
                  >
                    <span style={{ flex: 1 }}>
                      {hospitalUuid
                        ? hospitals.find((h) => h.hospitalUuid === hospitalUuid)
                          ? `${hospitals.find((h) => h.hospitalUuid === hospitalUuid)!.hospitalName} (${hospitals.find((h) => h.hospitalUuid === hospitalUuid)!.hospitalCode})`
                          : 'Select a hospital'
                        : 'Select a hospital'}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>▼</span>
                  </div>
                  {hospitalDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 50,
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        marginTop: '2px',
                        maxHeight: '260px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <div style={{ padding: '0.5rem' }}>
                        <input
                          type="text"
                          placeholder="Search hospitals..."
                          value={hospitalSearch}
                          onChange={(e) => setHospitalSearch(e.target.value)}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '0.4rem 0.5rem',
                            fontSize: '0.85rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                          aria-label="Search hospitals"
                        />
                      </div>
                      <div style={{ overflowY: 'auto', maxHeight: '200px' }}>
                        {hospitals
                          .filter((h) => {
                            const term = hospitalSearch.toLowerCase();
                            return (
                              h.hospitalName.toLowerCase().includes(term) ||
                              h.hospitalCode.toLowerCase().includes(term)
                            );
                          })
                          .map((h) => (
                            <div
                              key={h.hospitalUuid}
                              onClick={() => {
                                setHospitalUuid(h.hospitalUuid);
                                setHospitalDropdownOpen(false);
                                setHospitalSearch('');
                              }}
                              style={{
                                padding: '0.5rem 0.75rem',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                backgroundColor: h.hospitalUuid === hospitalUuid ? '#eff6ff' : 'transparent',
                                borderBottom: '1px solid #f3f4f6',
                              }}
                              onMouseEnter={(e) => { (e.target as HTMLDivElement).style.backgroundColor = '#f3f4f6'; }}
                              onMouseLeave={(e) => { (e.target as HTMLDivElement).style.backgroundColor = h.hospitalUuid === hospitalUuid ? '#eff6ff' : 'transparent'; }}
                            >
                              {h.hospitalName} ({h.hospitalCode})
                            </div>
                          ))}
                        {hospitals.filter((h) => {
                          const term = hospitalSearch.toLowerCase();
                          return h.hospitalName.toLowerCase().includes(term) || h.hospitalCode.toLowerCase().includes(term);
                        }).length === 0 && (
                          <div style={{ padding: '0.5rem 0.75rem', color: '#9ca3af', fontSize: '0.85rem' }}>
                            No hospitals found.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {fieldErrors.hospitalUuid && (
                  <span className="field-error" role="alert">{fieldErrors.hospitalUuid}</span>
                )}
              </div>
            </div>

            <div className="date-fields">
              <Input
                label="Valid From"
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                error={fieldErrors.validFrom}
                required
              />

              <Input
                label="Valid Till"
                type="date"
                value={validTill}
                onChange={(e) => setValidTill(e.target.value)}
                error={fieldErrors.validTill}
                required
              />
            </div>
          </div>
        </fieldset>

        {/* Input Parameters Section */}
        <fieldset>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <legend style={{ margin: 0 }}>Input Parameters</legend>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {lastParamReloadTime && (
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  Last updated: {lastParamReloadTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              )}
              <Button
                type="button"
                onClick={reloadParameterList}
                isLoading={isReloadingParams}
                disabled={isReloadingParams}
                size="sm"
                style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
              >
                {isReloadingParams ? 'Reloading...' : '🔄 Reload Parameters'}
              </Button>
            </div>
          </div>

          {availableInputParams.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>No input parameters available.</p>
          ) : (
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              {/* Available parameters with checkboxes */}
              <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', gap: '0.5rem' }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', margin: 0 }}>
                    Available Parameters
                  </h4>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={paramSearchTerm}
                    onChange={(e) => setParamSearchTerm(e.target.value)}
                    aria-label="Search available parameters"
                    style={{
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.8rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      outline: 'none',
                      width: '140px',
                    }}
                  />
                </div>
                <div
                  style={{
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    maxHeight: '240px',
                    overflowY: 'auto',
                  }}
                >
                  {availableInputParams
                    .filter((param) => param.parameterName.toLowerCase().includes(paramSearchTerm.toLowerCase()))
                    .map((param) => (
                    <label
                      key={param.parameterId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        backgroundColor: selectedInputParamIds.includes(param.parameterId) ? '#eff6ff' : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedInputParamIds.includes(param.parameterId)}
                        onChange={() => toggleInputParam(param.parameterId)}
                      />
                      <span>{param.parameterName}</span>
                      <span style={{
                        fontSize: '0.7rem',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '9999px',
                        marginLeft: 'auto',
                        backgroundColor: (param.sourceType === 'SYSTEM' || !param.sourceType) ? '#eff6ff' : '#f0fdf4',
                        color: (param.sourceType === 'SYSTEM' || !param.sourceType) ? '#1e40af' : '#166534',
                      }}>
                        {(param.sourceType === 'SYSTEM' || !param.sourceType) ? 'SYSTEM' : 'MANUAL'}
                      </span>
                      <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                        {param.dataType}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Selected parameters with drag-and-drop reordering */}
              <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Selected Parameters (drag to reorder)
                </h4>
                {selectedInputParamIds.length === 0 ? (
                  <p style={{ color: '#9ca3af', fontSize: '0.85rem', fontStyle: 'italic' }}>
                    No parameters selected.
                  </p>
                ) : (
                  <div
                    style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      maxHeight: '240px',
                      overflowY: 'auto',
                    }}
                  >
                    {selectedInputParamIds.map((paramId, index) => {
                      const param = availableInputParams.find((p) => p.parameterId === paramId);
                      if (!param) return null;
                      return (
                        <div
                          key={paramId}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={handleDragOver}
                          onDrop={() => handleDrop(index)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 0.75rem',
                            borderBottom: '1px solid #f3f4f6',
                            cursor: 'grab',
                            fontSize: '0.9rem',
                            backgroundColor: '#fff',
                          }}
                        >
                          <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>☰</span>
                          <span style={{ flex: 1 }}>{param.parameterName}</span>
                          <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{param.dataType}</span>
                          <span
                            style={{
                              fontSize: '0.7rem',
                              padding: '0.1rem 0.4rem',
                              borderRadius: '9999px',
                              backgroundColor: (param.sourceType === 'SYSTEM' || !param.sourceType) ? '#eff6ff' : '#f0fdf4',
                              color: (param.sourceType === 'SYSTEM' || !param.sourceType) ? '#1e40af' : '#166534',
                            }}
                          >
                            {(param.sourceType === 'SYSTEM' || !param.sourceType) ? 'SYSTEM' : 'MANUAL'}
                          </span>
                          <span
                            style={{
                              fontSize: '0.75rem',
                              padding: '0.1rem 0.4rem',
                              borderRadius: '9999px',
                              backgroundColor: param.validationRules?.required ? '#fef2f2' : '#f0fdf4',
                              color: param.validationRules?.required ? '#991b1b' : '#166534',
                            }}
                          >
                            {param.validationRules?.required ? 'Required' : 'Optional'}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeInputParam(paramId)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#ef4444',
                              fontSize: '1rem',
                              padding: '0 0.25rem',
                              lineHeight: 1,
                            }}
                            title="Remove parameter"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </fieldset>

        {/* Calculated Parameters Section */}
        <fieldset>
          <legend>Calculated Parameters</legend>

          {fieldErrors.calcParamNames && (
            <Alert type="error" onDismiss={() => setFieldErrors((prev) => { const u = { ...prev }; delete u.calcParamNames; return u; })}>
              {fieldErrors.calcParamNames}
            </Alert>
          )}

          {calcParams.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              No calculated parameters defined. Click below to add one.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {calcParams.map((cp, index) => {
                const refs = getReferencedParams(cp.naturalLanguageFormula);
                return (
                  <div
                    key={index}
                    style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '0.75rem',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>
                        #{index + 1}
                      </span>
                      <div style={{ flex: 1 }} />
                      <button
                        type="button"
                        onClick={() => removeCalcParam(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#ef4444',
                          fontSize: '0.85rem',
                          padding: '0.1rem 0.4rem',
                        }}
                        title="Remove calculated parameter"
                      >
                        ✕ Remove
                      </button>
                    </div>

                    <Input
                      label="Parameter Name"
                      value={cp.parameterName}
                      onChange={(e) => updateCalcParam(index, 'parameterName', e.target.value)}
                      placeholder="e.g., Adjusted_Amount"
                    />

                    <FormulaTextArea
                      label="Natural Language Formula"
                      value={cp.naturalLanguageFormula}
                      onChange={(e) => updateCalcParam(index, 'naturalLanguageFormula', e.target.value)}
                      rows={3}
                      placeholder="e.g., if @Gross_Bill is greater than 50000 then @Net_Amount * 0.20 (type @ to see variables)"
                      variables={getKnownVariableNames()}
                      savedFormulas={getSavedFormulasMap()}
                    />

                    {cp.executableFormula !== undefined && (
                      <FormulaTextArea
                        label="Executable Formula"
                        value={cp.executableFormula || ''}
                        onChange={(e) => updateCalcParam(index, 'executableFormula', e.target.value)}
                        rows={2}
                        placeholder="Generated executable formula (or type manually with @ for variables)"
                        variables={getKnownVariableNames()}
                        savedFormulas={getSavedFormulasMap()}
                      />
                    )}

                    {refs.length > 0 && (
                      <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        References: {refs.join(', ')}
                      </p>
                    )}

                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => handleConvertCalcParam(index)}
                        disabled={!cp.naturalLanguageFormula.trim() || convertingCalcIndex === index}
                        isLoading={convertingCalcIndex === index}
                      >
                        Convert to Formula
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setTestCalcIndex(index)}
                        disabled={!cp.naturalLanguageFormula.trim()}
                      >
                        Test Formula
                      </Button>
                    </div>

                    {calcConvertErrors[index] && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <Alert type="error" onDismiss={() => setCalcConvertErrors((prev) => {
                          const updated = { ...prev };
                          delete updated[index];
                          return updated;
                        })}>
                          {calcConvertErrors[index]}
                        </Alert>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: '0.75rem' }}>
            <Button type="button" variant="secondary" onClick={addCalcParam}>
              + Add Calculated Parameter
            </Button>
          </div>
        </fieldset>

        {/* Hospital Share & AR/AP Formulas */}
        <fieldset>
          <legend>Formulas</legend>

          <FormulaTextArea
            label="Hospital Share Formula (Natural Language)"
            value={hospitalShareFormula}
            onChange={(e) => setHospitalShareFormula(e.target.value)}
            error={fieldErrors.hospitalShareFormula}
            rows={4}
            placeholder="e.g., if @Gross_Bill is greater than 50000 then @Net_Amount * 0.20 (type @ to see variables)"
            required
            variables={getKnownVariableNames()}
            savedFormulas={getSavedFormulasMap()}
          />

          <FormulaTextArea
            label="Hospital Share Executable Formula"
            value={hospitalShareExecutable}
            onChange={(e) => setHospitalShareExecutable(e.target.value)}
            rows={3}
            placeholder="Generated executable formula (or type manually with @ for variables)"
            variables={getKnownVariableNames()}
            savedFormulas={getSavedFormulasMap()}
          />

          {getReferencedParams(hospitalShareFormula).length > 0 && (
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
              References: {getReferencedParams(hospitalShareFormula).join(', ')}
            </p>
          )}

          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleConvertHospitalShare}
              disabled={!hospitalShareFormula.trim() || isConvertingHs}
              isLoading={isConvertingHs}
            >
              Convert to Formula
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowHsTest(true)}
              disabled={!hospitalShareFormula.trim()}
            >
              Test Formula
            </Button>
          </div>

          {hsConvertError && (
            <div style={{ marginTop: '0.5rem' }}>
              <Alert type="error" onDismiss={() => setHsConvertError(null)}>
                {hsConvertError}
              </Alert>
            </div>
          )}

          <div style={{ marginTop: '1rem' }} />

          <FormulaTextArea
            label="AR/AP Formula (Natural Language)"
            value={arApFormula}
            onChange={(e) => setArApFormula(e.target.value)}
            error={fieldErrors.arApFormula}
            rows={4}
            placeholder="e.g., @Hospital_Share minus @Collection_Amount (type @ to see variables)"
            required
            variables={getKnownVariableNames()}
            savedFormulas={getSavedFormulasMap()}
          />

          <FormulaTextArea
            label="AR/AP Executable Formula"
            value={arApExecutable}
            onChange={(e) => setArApExecutable(e.target.value)}
            rows={3}
            placeholder="Generated executable formula (or type manually with @ for variables)"
            variables={getKnownVariableNames()}
            savedFormulas={getSavedFormulasMap()}
          />

          {getReferencedParams(arApFormula).length > 0 && (
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
              References: {getReferencedParams(arApFormula).join(', ')}
            </p>
          )}

          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleConvertArAp}
              disabled={!arApFormula.trim() || isConvertingArAp}
              isLoading={isConvertingArAp}
            >
              Convert to Formula
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowArApTest(true)}
              disabled={!arApFormula.trim()}
            >
              Test Formula
            </Button>
          </div>

          {arApConvertError && (
            <div style={{ marginTop: '0.5rem' }}>
              <Alert type="error" onDismiss={() => setArApConvertError(null)}>
                {arApConvertError}
              </Alert>
            </div>
          )}
        </fieldset>

        {/* Documents Section */}
        <fieldset>
          <legend>Documents <span style={{ color: '#dc2626', fontWeight: 'normal' }}>*</span></legend>

          {fieldErrors.documents && (
            <Alert type="error" onDismiss={() => setFieldErrors((prev) => { const u = { ...prev }; delete u.documents; return u; })}>
              {fieldErrors.documents}
            </Alert>
          )}

          <div className="file-upload-section">
            <label className="file-upload-label">
              Upload Document (PNG/JPEG/PDF, max 10 MB)
            </label>
            <div className="file-upload-wrapper">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,application/pdf"
                onChange={handleFileUpload}
                disabled={isUploading}
                className="file-upload-input"
                id="document-upload"
              />
              <label htmlFor="document-upload" className="file-upload-button">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Choose File
              </label>
              {isUploading && <LoadingSpinner size="sm" message="Uploading..." />}
            </div>
          </div>

          {/* Pending files (not yet uploaded to server) */}
          {pendingFiles.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
              <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                Pending upload (will be uploaded when agreement is saved):
              </p>
              {pendingFiles.map((file, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px dashed #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: '#fffbeb',
                  }}
                >
                  <span style={{ flex: 1, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {formatFileSize(file.size)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                    style={{
                      background: 'none',
                      border: '1px solid #fca5a5',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      color: '#ef4444',
                    }}
                    title="Remove"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Already uploaded documents */}
          {documents.length === 0 && pendingFiles.length === 0 && (
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', fontStyle: 'italic' }}>
              No documents uploaded.
            </p>
          )}

          {documents.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
              {documents.map((doc) => (
                <div
                  key={doc.documentId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: '#fafafa',
                  }}
                >
                  {doc.fileType === 'PDF' ? (
                    <div style={{ width: 48, height: 48, borderRadius: '4px', border: '1px solid #e5e7eb', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#dc2626' }}>
                      PDF
                    </div>
                  ) : thumbnailUrls[doc.documentId] ? (
                    <img
                      src={thumbnailUrls[doc.documentId]}
                      alt={doc.fileName}
                      style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                    />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: '4px', border: '1px solid #e5e7eb', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: '#9ca3af' }}>
                      {doc.fileType}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.fileName}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {doc.fileType} · {formatFileSize(doc.fileSize)} · {new Date(doc.uploadedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDownloadDocument(doc.documentId, doc.fileName)}
                    style={{
                      background: 'none',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      color: '#2563eb',
                    }}
                    title="Download"
                  >
                    Download
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteDocument(doc.documentId)}
                    style={{
                      background: 'none',
                      border: '1px solid #fca5a5',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      color: '#ef4444',
                    }}
                    title="Delete"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </fieldset>

        <div className="form-actions">
          <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
            {isEditMode ? 'Update Agreement' : 'Create Agreement'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/agreements')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {submitProgress && (
            <div style={{
              marginLeft: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#eff6ff',
              color: '#1e40af',
              borderRadius: '6px',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ 
                display: 'inline-block',
                animation: 'spin 1s linear infinite'
              }}>⟳</span>
              {submitProgress}
            </div>
          )}
        </div>
      </form>

      {showHsTest && (
        <AgreementFormulaTestDialog
          isOpen={showHsTest}
          onClose={() => setShowHsTest(false)}
          formula={hospitalShareFormula}
          formulaLabel="Hospital Share"
          knownVariables={getKnownVariableNames()}
        />
      )}

      {showArApTest && (
        <AgreementFormulaTestDialog
          isOpen={showArApTest}
          onClose={() => setShowArApTest(false)}
          formula={arApFormula}
          formulaLabel="AR/AP"
          knownVariables={getKnownVariableNames()}
        />
      )}

      {testCalcIndex !== null && calcParams[testCalcIndex] && (
        <AgreementFormulaTestDialog
          isOpen={true}
          onClose={() => setTestCalcIndex(null)}
          formula={calcParams[testCalcIndex].naturalLanguageFormula}
          formulaLabel={calcParams[testCalcIndex].parameterName || `Calculated #${testCalcIndex + 1}`}
          knownVariables={getKnownVariableNames()}
        />
      )}
    </div>
  );
}
